// Level2Logic (cmmc20) — Client Feedback Readiness
// Analytics hooks + feedback data layer
// Tracks page views, feature usage

// ─── Types ────────────────────────────────────────────────────────────────

export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, unknown>;
  timestamp: Date;
}

export interface FeedbackData {
  rating: number;
  comment: string;
  feature: string;
  timestamp: Date;
}

// ─── Analytics Service ────────────────────────────────────────────────────

const ANALYTICS_STORAGE_KEY = 'l2l_analytics';
const FEEDBACK_SHOWN_KEY = 'l2l_feedback_shown';
const PAGE_VIEW_THRESHOLD = 5;
const SESSION_DURATION_MS = 5 * 60 * 1000;

export function trackPageView(path: string): void {
  if (typeof window === 'undefined') return;
  const views = getStoredViews();
  views.push({ path, timestamp: Date.now() });
  const trimmed = views.slice(-100);
  localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(trimmed));
}

export function trackFeatureUse(feature: string): void {
  if (typeof window === 'undefined') return;
  const key = `l2l_feature_${feature}`;
  const count = parseInt(localStorage.getItem(key) || '0') + 1;
  localStorage.setItem(key, count.toString());
}

export function getStoredViews(): Array<{ path: string; timestamp: number }> {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(ANALYTICS_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

export function shouldShowFeedback(): boolean {
  if (typeof window === 'undefined') return false;
  if (localStorage.getItem(FEEDBACK_SHOWN_KEY) === 'true') return false;

  const views = getStoredViews();
  const recentViews = views.filter(v => Date.now() - v.timestamp < SESSION_DURATION_MS);

  return recentViews.length >= PAGE_VIEW_THRESHOLD;
}

export function markFeedbackShown(): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(FEEDBACK_SHOWN_KEY, 'true');
}

export function submitFeedback(data: FeedbackData): void {
  markFeedbackShown();
  if (import.meta.env.DEV) {
    console.debug('[Feedback]', data);
  }
}
